/**
 * سندِ پرداخت / دریافت — **یک فرمِ واحد** برای همهٔ حسابداری.
 *
 * خواستهٔ کاربر (۱۴۰۵/۰۶/۲۱): «نوعِ تراکنش گنگه؛ اول مشخص باشه دریافته یا پرداخته؛ پرداخت
 * مبدأ داشته باشه، دریافت مشخص باشه از چه شخصیه؛ اضافه‌کار تو حسابش مثبت باشه، کسر
 * غیبت منفی؛ یه سندِ ثابت که تو تمام قسمت‌ها استفاده بشه.»
 *
 * سه نوع سند:
 *   • پرداخت به شخص  — پول از ما به او (مبدأ: دفتر/تنخواه) → حسابش **بدهکار** (−)
 *   • دریافت از شخص  — پول از او به ما (به حساب: دفتر/تنخواه) → حسابش **بستانکار** (+)
 *   • قلمِ حقوقی     — پولی جابه‌جا نمی‌شود؛ فقط حقوقِ ماه زیاد (+) یا کم (−) می‌شود
 * زیرِ کاپوت همان `transaction_type`های دفترِ مالی‌اند؛ هیچ منطقِ محاسبه‌ای عوض نشده.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Form, Input, InputNumber, Modal, Segmented, Select, Space, Typography, message } from "antd";
import { createFinanceTxn, createPayrollItem, getEmployees, getPeriods, updateFinanceTxn } from "../api/client";
import SourceSelect, { OFFICE, sourceOf } from "./SourceSelect";
import { apiError } from "../utils/apiError";
import { JALALI_MONTHS, JalaliDateInput, todayISO } from "../utils/dateUtils";
import { MoneyInput, formatMoney } from "../utils/money";
import { filterByChildren } from "../utils/selectFilter";
import confirmEntry from "../utils/entryCheck";

const { Text } = Typography;

export const KIND_LABEL = { pay: "پرداخت به شخص", receive: "دریافت از شخص", payroll: "قلمِ حقوقی" };
export const KIND_COLOR = { pay: "volcano", receive: "green", payroll: "geekblue" };

/** موضوعِ سند → نوعِ دفترِ مالی + اثرِ روشن روی حساب */
export const SUBJECTS = [
  { v: "advance", kind: "pay", l: "مساعده", effect: "debit", note: "از حقوقِ همین ماه کم می‌شود" },
  { v: "bank_transfer", kind: "pay", l: "حواله بانکی / کارت‌به‌کارت", effect: "debit", note: "پرداخت به شخص؛ حسابش بدهکار می‌شود" },
  { v: "check_payment", kind: "pay", l: "چک", effect: "debit", note: "پرداخت با چک؛ حسابش بدهکار می‌شود" },
  { v: "salary_paid", kind: "pay", l: "پرداخت حقوق", effect: "debit", note: "پرداختِ حقوقِ ماه (ترجیحاً از تبِ «پرداخت حقوق»)" },
  { v: "cash_repayment", kind: "receive", l: "بازپرداختِ بدهی", effect: "credit", note: "از شخص گرفتیم؛ بدهی‌اش کم می‌شود" },
  { v: "refund", kind: "receive", l: "برگشت وجه", effect: "credit", note: "از شخص برگشت؛ حسابش بستانکار می‌شود" },
  /* v2.144.0 — «مثلِ همان سندِ محاسبه و بستن حقوق: ساعتی، کارکرد، مبلغ» ⇒ مستقیم ردیفِ همان صفحه و حقوقِ همان نفر
     خودکار دوباره حساب می‌شود (بدونِ رفتن به محاسبه/بستن/محاسبهٔ مجدد). */
  { v: "adj_hourly", kind: "payroll", adj: "hourly", dir: "earn", l: "اضافه‌کار ساعتی (ساعت × نرخ)", effect: "credit",
    unit: "ساعت", note: "در «محاسبه و بستن حقوق»ِ همان ماه می‌نشیند و حقوق خودکار حساب می‌شود" },
  { v: "adj_daily", kind: "payroll", adj: "daily", dir: "earn", l: "کارکردِ روزمزد (روز × نرخ)", effect: "credit",
    unit: "روز", note: "در «محاسبه و بستن حقوق»ِ همان ماه می‌نشیند و حقوق خودکار حساب می‌شود" },
  { v: "adj_piece", kind: "payroll", adj: "piece", dir: "earn", l: "کارِ کارمزدی (تعداد × فی)", effect: "credit",
    unit: "تعداد", note: "در «محاسبه و بستن حقوق»ِ همان ماه می‌نشیند و حقوق خودکار حساب می‌شود" },
  { v: "adj_earn", kind: "payroll", adj: "amount", dir: "earn", l: "اضافه در حقوقِ ماه (مبلغ)", effect: "credit",
    note: "در «محاسبه و بستن حقوق»ِ همان ماه می‌نشیند و حقوق خودکار حساب می‌شود" },
  { v: "adj_deduct", kind: "payroll", adj: "amount", dir: "deduct", l: "کسر از حقوقِ ماه (مبلغ)", effect: "debit",
    note: "در «محاسبه و بستن حقوق»ِ همان ماه کم می‌شود و حقوق خودکار حساب می‌شود" },
  { v: "overtime_bonus", kind: "payroll", l: "اضافه‌کار (مبلغِ یک‌جا)", effect: "credit", note: "به حقوقِ ماه اضافه می‌شود" },
  { v: "reward", kind: "payroll", l: "پاداش", effect: "credit", note: "به حقوقِ ماه اضافه می‌شود" },
  { v: "manual_credit", kind: "payroll", l: "بستانکاری دستی", effect: "credit", note: "به طلبِ شخص اضافه می‌شود" },
  { v: "absence_manual", kind: "payroll", l: "کسر غیبت", effect: "debit", note: "از حقوقِ ماه کم می‌شود" },
  { v: "shortage_manual", kind: "payroll", l: "کسر کار", effect: "debit", note: "از حقوقِ ماه کم می‌شود" },
  { v: "penalty", kind: "payroll", l: "جریمه", effect: "debit", note: "از حقوقِ ماه کم می‌شود" },
  { v: "manual_deduction", kind: "payroll", l: "کسر دستی", effect: "debit", note: "از حقوقِ ماه کم می‌شود" },
  { v: "buffet_purchase", kind: "payroll", l: "خرید بوفه / سوپرمارکت", effect: "debit", purchase: true, note: "بهای خرید از حقوقِ ماه کم می‌شود" },
  { v: "product_purchase", kind: "payroll", l: "خرید محصول / کالا", effect: "debit", purchase: true, note: "بهای خرید از حقوقِ ماه کم می‌شود" },
  { v: "defective_product_purchase", kind: "payroll", l: "خرید محصول زده‌دار", effect: "debit", purchase: true, note: "بهای خرید از حقوقِ ماه کم می‌شود" },
  { v: "previous_debt", kind: "payroll", l: "بدهی قبلی", effect: "debit", note: "ماندهٔ بدهیِ قبلی که از حقوق کم می‌شود" },
];
const BY_TYPE = Object.fromEntries(SUBJECTS.map((s) => [s.v, s]));
export const subjectOf = (type) => BY_TYPE[type];
export const kindOfType = (type) => BY_TYPE[type]?.kind || "payroll";

const METHODS = [
  { value: "cash", label: "نقدی" }, { value: "bank", label: "کارت‌به‌کارت / حواله" },
  { value: "check", label: "چک" }, { value: "other", label: "سایر" },
];

let empCache = null;

export default function VoucherModal({ open, onClose, onSaved, employees, initial = {}, periodId }) {
  const [form] = Form.useForm();
  const [kind, setKind] = useState("pay");
  const [saving, setSaving] = useState(false);
  const [emps, setEmps] = useState(employees || empCache || []);
  const [periods, setPeriods] = useState([]);
  const editing = !!initial?.id;
  useEffect(() => { if (open) getPeriods().then((r) => setPeriods(r.data || [])).catch(() => {}); }, [open]);
  // ماه‌های باز (تأیید/قفل‌نشده) — قلمِ حقوقی فقط به ماهِ باز می‌رود
  const openPeriods = useMemo(() => periods.filter((p) => !["approved", "paid", "locked"].includes(p.status)
    && p.closing_status !== "locked").sort((a, b) => (b.year - a.year) || (b.month - a.month)), [periods]);

  useEffect(() => {
    if (employees?.length) { setEmps(employees); return; }
    if (empCache) { setEmps(empCache); return; }
    getEmployees().then((r) => { empCache = r.data || []; setEmps(empCache); }).catch(() => {});
  }, [employees]);

  // با هر بازشدن، فرم از `initial` پر می‌شود
  useEffect(() => {
    if (!open) return;
    const k = initial.kind || kindOfType(initial.transaction_type || "bank_transfer");
    setKind(k);
    form.resetFields();
    form.setFieldsValue({
      employee_id: initial.employee_id ?? undefined,
      transaction_type: initial.transaction_type || (k === "pay" ? "bank_transfer" : k === "receive" ? "cash_repayment" : "adj_hourly"),
      amount: initial.amount ?? null,
      occurred_at: initial.occurred_at || todayISO(),
      description: initial.description || "",
      payment_method: initial.payment_method || (k === "payroll" ? undefined : "cash"),
      reference_number: initial.reference_number || "",
      item_name: initial.item_name || "", quantity: initial.quantity ?? null, unit_price: initial.unit_price ?? null,
      source: initial.source_account ? sourceOf(initial) : OFFICE,
    });
  }, [open]);
  useEffect(() => {
    if (!open || form.getFieldValue("adj_period_id")) return;
    const pick = openPeriods.find((p) => p.id === periodId) || openPeriods[0];
    if (pick) form.setFieldsValue({ adj_period_id: pick.id });
  }, [open, openPeriods]);   // eslint-disable-line

  const type = Form.useWatch("transaction_type", form);
  const amount = Form.useWatch("amount", form);
  const empId = Form.useWatch("employee_id", form);
  const src = Form.useWatch("source", form);
  const subj = subjectOf(type);
  const qty = Form.useWatch("adj_qty", form);
  const rate = Form.useWatch("adj_rate", form);
  const isAdj = !!subj?.adj;
  const adjQty = isAdj && subj.adj !== "amount";
  const adjAmount = adjQty ? Math.round((Number(qty) || 0) * (Number(rate) || 0)) : null;
  const subjects = useMemo(() => SUBJECTS.filter((s) => s.kind === kind), [kind]);

  const changeKind = (k) => {
    setKind(k);
    const first = SUBJECTS.find((s) => s.kind === k);
    form.setFieldsValue({ transaction_type: first?.v,
      payment_method: k === "payroll" ? undefined : (form.getFieldValue("payment_method") || "cash") });
  };

  const empName = emps.find((e) => e.id === empId)?.full_name || "شخص";
  const amt = adjQty ? adjAmount : Number(amount || 0);
  const effect = subj ? (subj.effect === "credit" ? "+" : "−") : "";
  const cashy = kind !== "payroll";

  const submit = async (v) => {
    // جریمه بدونِ کد مدیریتی ثبت می‌شود (v2.109.0)
    // v2.173.0 — پیش از ثبت: صفرِ اضافه، تکراری، اسمِ همکار در سندِ یک‌طرفه
    const ok = await confirmEntry({
      employee_id: editing ? initial.employee_id : v.employee_id,
      amount: isAdj && adjQty ? adjAmount : (v.amount || (v.quantity && v.unit_price ? v.quantity * v.unit_price : 0)),
      transaction_type: editing ? initial.transaction_type : v.transaction_type,
      description: v.description || null, occurred_at: v.occurred_at || todayISO(),
      exclude_txn_id: editing ? initial.id : null,
    });
    if (!ok) return;
    setSaving(true);
    try {
      const source = cashy ? (v.source || OFFICE) : {};
      if (editing) {
        await updateFinanceTxn(initial.id, {
          amount: v.amount || 0, occurred_at: v.occurred_at || todayISO(),
          description: v.description || null, payment_method: v.payment_method || null,
          reference_number: v.reference_number || null,
          item_name: v.item_name || null, quantity: v.quantity ?? null, unit_price: v.unit_price ?? null,
          ...source,
        });
        message.success("سند ویرایش شد");
      } else if (isAdj) {
        const r = await createPayrollItem({
          employee_id: v.employee_id, period_id: v.adj_period_id, direction: subj.dir, kind: subj.adj,
          title: (v.adj_title || "").trim() || subj.l.replace(/ \(.*\)$/, ""),
          quantity: adjQty ? v.adj_qty : null, unit_price: adjQty ? v.adj_rate : null,
          amount: adjQty ? null : v.amount, note: v.description || null,
        });
        message.success(r.data?.message || "در حقوقِ ماه ثبت شد", 6);
      } else {
        await createFinanceTxn({
          employee_id: v.employee_id, payroll_period_id: periodId || null,
          transaction_type: v.transaction_type, amount: v.amount || 0,
          occurred_at: v.occurred_at || todayISO(), description: v.description || null,
          payment_method: cashy ? (v.payment_method || null) : null,
          reference_number: v.reference_number || null,
          item_name: v.item_name || null, quantity: v.quantity ?? null, unit_price: v.unit_price ?? null,
          ...source,
        });
        message.success(kind === "pay" ? "پرداخت ثبت شد" : kind === "receive" ? "دریافت ثبت شد" : "قلمِ حقوقی ثبت شد");
      }
      onSaved && onSaved();
      onClose();
    } catch (e) { message.error(apiError(e, "ثبت نشد")); }
    setSaving(false);
  };

  return (
    <Modal open={open} onCancel={onClose} onOk={() => form.submit()} confirmLoading={saving}
      title={editing ? `ویرایش سند ${initial.doc_no || ""}` : "سند پرداخت / دریافت"}
      okText={editing ? "ذخیره" : "ثبت سند"} cancelText="انصراف" width={620} destroyOnHidden>
      <Segmented block value={kind} onChange={changeKind} disabled={editing} style={{ marginBottom: 12 }}
        options={[
          { value: "pay", label: "پرداخت به شخص" },
          { value: "receive", label: "دریافت از شخص" },
          { value: "payroll", label: "قلمِ حقوقی (+ / −)" },
        ]} />
      <Form form={form} layout="vertical" onFinish={submit} style={{ direction: "rtl" }}>
        <Form.Item name="employee_id" label="شخص" rules={[{ required: true, message: "شخص را انتخاب کنید" }]}>
          <Select showSearch disabled={editing} placeholder="جست‌وجوی نام یا کد" filterOption={filterByChildren}
            options={emps.map((e) => ({ value: e.id, label: `${e.full_name}${e.employee_code ? ` (${e.employee_code})` : ""}` }))} />
        </Form.Item>
        <Form.Item name="transaction_type" label="موضوع" rules={[{ required: true }]}>
          <Select disabled={editing}
            options={subjects.map((s) => ({ value: s.v,
              label: `${s.l}  ${s.effect === "credit" ? "(+)" : "(−)"}` }))} />
        </Form.Item>
        {isAdj && (
          <>
            <Space wrap align="start">
              <Form.Item name="adj_period_id" label="در حقوقِ کدام ماه؟" rules={[{ required: true, message: "ماه را انتخاب کنید" }]}
                extra={!openPeriods.length ? "هیچ ماهِ بازی نیست (ماه‌های بسته قلمِ جدید نمی‌گیرند)" : undefined}>
                <Select style={{ width: 170 }} options={openPeriods.map((p) => ({ value: p.id,
                  label: `${JALALI_MONTHS[p.month - 1]} ${p.year}` }))} />
              </Form.Item>
              <Form.Item name="adj_title" label="شرحِ ردیف (در فیش)">
                <Input style={{ width: 220 }} placeholder={subj.l.replace(/ \(.*\)$/, "")} />
              </Form.Item>
            </Space>
            {adjQty && (
              <Space wrap align="start">
                <Form.Item name="adj_qty" label={subj.unit} rules={[{ required: true, message: "لازم است" }]}
                  extra={subj.adj === "hourly" ? "مثلاً ۲٫۵ = دو ساعت و نیم" : undefined}>
                  <InputNumber min={0} step={subj.adj === "hourly" ? 0.25 : 1} style={{ width: 120 }} />
                </Form.Item>
                <Form.Item name="adj_rate" label={subj.adj === "hourly" ? "نرخِ هر ساعت (ریال)" : subj.adj === "daily" ? "نرخِ هر روز (ریال)" : "فی (ریال)"}
                  rules={[{ required: true, message: "لازم است" }]}>
                  <MoneyInput style={{ width: 180 }} />
                </Form.Item>
                <Form.Item label="مبلغ"><b style={{ lineHeight: "32px" }}>{formatMoney(adjAmount || 0)} ریال</b></Form.Item>
              </Space>
            )}
          </>
        )}
        {subj?.purchase && (
          <Space wrap>
            <Form.Item name="item_name" label="نام کالا"><Input style={{ width: 180 }} /></Form.Item>
            <Form.Item name="quantity" label="تعداد"><InputNumber min={0} style={{ width: 90 }} /></Form.Item>
            <Form.Item name="unit_price" label="قیمت واحد (ریال)"><MoneyInput style={{ width: 150 }} /></Form.Item>
          </Space>
        )}
        {!adjQty && (
        <Space wrap align="start" style={{ width: "100%" }}>
          <Form.Item name="amount" label="مبلغ (ریال)" rules={[{ required: !subj?.purchase, message: "مبلغ لازم است" }]}
            extra={subj?.purchase ? "خالی بگذارید تا تعداد × قیمت حساب شود" : undefined}>
            <MoneyInput words style={{ width: 200 }} />
          </Form.Item>
          {!isAdj && (
            <Form.Item name="occurred_at" label="تاریخ" rules={[{ required: true }]}>
              <JalaliDateInput style={{ width: 160 }} />
            </Form.Item>
          )}
        </Space>
        )}
        {cashy && (
          <Space wrap align="start">
            <Form.Item name="source" label={kind === "pay" ? "مبدأ پرداخت" : "واریز به حساب"}
              extra="تنخواه ⇒ همین سند خودکار در صندوق می‌نشیند">
              <SourceSelect style={{ width: 220 }} />
            </Form.Item>
            <Form.Item name="payment_method" label="روش"><Select options={METHODS} style={{ width: 170 }} /></Form.Item>
            <Form.Item name="reference_number" label="شمارهٔ پیگیری / چک"><Input style={{ width: 160 }} /></Form.Item>
          </Space>
        )}
        <Form.Item name="description" label="شرح"><Input.TextArea rows={2} /></Form.Item>
      </Form>
      {subj && (
        <Alert showIcon type={subj.effect === "credit" ? "success" : "warning"}
          message={
            <span>
              حسابِ <b>{empName}</b>: <b style={{ color: subj.effect === "credit" ? "#107C41" : "#C0392B" }}>
                {effect}{formatMoney(amt)}</b>
              {" — "}{subj.effect === "credit" ? "بستانکار می‌شود" : "بدهکار می‌شود"} ({subj.note})
              {cashy && src?.source_account === "petty" && (
                <Text type="secondary"> • {kind === "pay" ? "و از صندوقِ تنخواه کم می‌شود" : "و به صندوقِ تنخواه اضافه می‌شود"}</Text>
              )}
            </span>
          } />
      )}
    </Modal>
  );
}
